import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Box } from 'edenred-ui';

import config from '@epi-config';
import { FormFields } from '@epi-forms/helpers';
import { MoneyAmountField } from '@epi-forms/controls/MoneyAmountField/MoneyAmountField';
import { BenefitSelect } from '@epi-forms/controls/BenefitSelect/BenefitSelect';
import { Schedule } from '@epi-constants/benefitTypes';
import {
  useTopupRequest,
  useTopupEmployees,
  useTopupSwitch,
  useTopupPendingBenefits
} from '@epi-pages/TransferPage/hooks';
import { getNumber, formatCurrencyDecimalConvert } from '@epi-helpers/numeral';
import {
  required,
  yearlyLimit,
  minAmountMoreAccurate
} from '@epi-helpers/formHelpers/validators';
import { getBenefitSelected } from '@epi-selectors/benefitGroupSettings';
import { IUpdateFooter } from '@epi-models/pages/TransferPage';
import { getTopupTable } from '@epi-store/topup/topup.selectors';
import { isTomorrow } from '@epi-helpers/TopupHelper';
import { FormStepMode } from '@epi-models/pages/Transfer';

import { SimpleFormStep } from '../SimpleFormStep';
import { TopupSummary } from '../TopupSummary/TopupSummary';
import { useTopupSummary } from '../TopupSummary/useTopupSummary';
import { FormStepStyles } from '../FormStep/FormStep.styles';
import { FormStep } from '../FormStep';
import { FormStepLabel } from '../FormStepLabel';
import { TopupSwitch } from './TopupSwitch';

import { EmployeesTable } from './EmployeesTable';
import { TopupInProgress } from './TopupInProgress';
import { TopupConfirmationModal } from './TopupConfirmationModal';
import { FieldInfo, StepField } from './TopupForm.styles';
import { TopupShowTable } from './TopupShowTable';
import { TopupSchedule } from './TopupSchedule';
import { MyChoiceInfo } from './MyChoiceInfo';

const { enableScheduledLoad } = config;

export const TopupForm = ({
  updateFooter,
  form,
  loadableBenefits
}: IUpdateFooter) => {
  const { t } = useTranslation();
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [showTopupForm, setShowTopupForm] = useState(true);

  const showTable = useSelector(getTopupTable);

  const {
    watch,
    handleSubmit,
    setValue,
    resetField,
    formState: { isValid, errors }
  } = form;

  const benefitType = watch(FormFields.Benefit);
  const topupPerPerson = watch(FormFields.MoneyAmount);
  const scheduleType = watch(FormFields.ScheduleType);
  const benefitSettings = useSelector(state =>
    getBenefitSelected(state, benefitType)
  );
  const {
    paginationCount,
    toggleEmployee,
    checkedCount,
    employeesToTopup,
    employeesCount,
    lowestAvailable,
    topupBeneficiariesIds,
    toggleAllEmployee,
    isAllEmployeeChecked,
    setForceUpdate
  } = useTopupEmployees();

  const { handleToggler, isToggle, showOnHoldBtn } = useTopupSwitch();

  const { postTopupRequest, isTopupPending, isTopupProcessing, summaryId } =
    useTopupRequest(setShowConfirmationModal, isToggle, showOnHoldBtn);
  const { remainingBalance, formattedTopup, formattedTopupWithFees } =
    useTopupSummary(employeesCount, employeesToTopup);

  const { isPendingBenefit, getPendingBenefits } = useTopupPendingBenefits(
    topupBeneficiariesIds
  );

  const onSubmit = () => {
    if (scheduleType === Schedule.SelectDate) {
      getPendingBenefits();
    }
    setShowConfirmationModal(true);
  };
  const handleCloseModal = () => setShowConfirmationModal(false);

  const handleTopupRequest = () => {
    const payload = {
      amount: (getNumber(topupPerPerson) * 100).toFixed(0),
      benefitType,
      beneficiariesIds: topupBeneficiariesIds
    };
    postTopupRequest(payload);
    resetField(FormFields.MoneyAmount);
    resetField(FormFields.Benefit);
  };

  useEffect(() => {
    if (loadableBenefits?.length === 1) {
      setValue(FormFields.Benefit, loadableBenefits[0]);
    }
  }, [summaryId]);

  useEffect(() => {
    if (isTopupProcessing) {
      setShowTopupForm(false);
    }
  }, [isTopupProcessing]);

  useEffect(() => {
    if (showTopupForm) {
      updateFooter({
        submitText: t('containers.deposit_money.topup_tab.footer_button'),
        onSubmit: handleSubmit(onSubmit),
        disableSubmit:
          !isValid ||
          remainingBalance < 0 ||
          !topupBeneficiariesIds.length ||
          (scheduleType === Schedule.SelectDate &&
            !isTomorrow(watch(FormFields.ScheduleDate)))
      });
    }
    if (!showTopupForm) {
      updateFooter(undefined);
    }
  }, [showTopupForm, isValid, remainingBalance, topupBeneficiariesIds]);

  return (
    <>
      {!showTopupForm ? (
        <TopupInProgress
          setShowTopupForm={setShowTopupForm}
          isTopupProcessing={isTopupProcessing}
          summaryId={summaryId}
          loadableBenefits={loadableBenefits}
          setForceUpdate={setForceUpdate}
        />
      ) : (
        <>
          <MyChoiceInfo loadableBenefits={loadableBenefits} />
          <SimpleFormStep
            mode={FormStepMode.Outlined}
            stepNumber={1}
            stepTitle={t(
              'containers.deposit_money.topup_tab.form.benefit.title'
            )}
          >
            <StepField>
              <BenefitSelect options={loadableBenefits} />
              <FieldInfo id="deposit_money_topup_step_one_info">
                {t('containers.deposit_money.topup_tab.form.benefit.info')}
              </FieldInfo>
            </StepField>
          </SimpleFormStep>
          {enableScheduledLoad && (
            <SimpleFormStep
              mode={FormStepMode.Outlined}
              stepNumber={2}
              stepTitle={t(
                'containers.deposit_money.topup_tab.form.schedule.title'
              )}
              pr="70px"
            >
              <Box width={500} id="deposit_money_topup_step_two_schedule">
                <TopupSchedule />
              </Box>
            </SimpleFormStep>
          )}
          <SimpleFormStep
            mode={FormStepMode.Contained}
            stepNumber={enableScheduledLoad ? 3 : 2}
            stepTitle={t(
              'containers.deposit_money.topup_tab.form.amount.title'
            )}
          >
            <StepField>
              <MoneyAmountField
                validators={[
                  required,
                  yearlyLimit(lowestAvailable),
                  minAmountMoreAccurate(1)
                ]}
                labelKey={
                  'containers.deposit_money.topup_tab.form.amount.input'
                }
              />
              {!errors[FormFields.MoneyAmount] && (
                <FieldInfo id="deposit_money_topup_step_three_info">
                  {t('containers.deposit_money.topup_tab.form.amount.max', {
                    maxTopup: formatCurrencyDecimalConvert(
                      benefitSettings?.maxAmountPerPeriod ?? 0
                    )
                  })}
                </FieldInfo>
              )}
            </StepField>
          </SimpleFormStep>

          <FormStep mode={FormStepMode.Outlined} stepNumber={4}>
            <Box flex="1" width="100%">
              <FormStepStyles.Content>
                <Box
                  display="flex"
                  flexDirection="column"
                  alignSelf="stretch"
                  justifyContent="space-between"
                >
                  <FormStepLabel
                    mode={FormStepMode.Outlined}
                    stepNumber={enableScheduledLoad ? 4 : 3}
                    stepTitle={t(
                      'containers.deposit_money.topup_tab.form.employees.title',
                      {
                        employeesNumber: topupBeneficiariesIds.length
                      }
                    )}
                  >
                    {!!paginationCount && <TopupShowTable />}
                  </FormStepLabel>
                </Box>
                <TopupSummary
                  employeesCount={employeesCount}
                  employeesToTopup={employeesToTopup}
                />
              </FormStepStyles.Content>
              {showOnHoldBtn && (
                <TopupSwitch checked={isToggle} onChange={handleToggler} />
              )}
              {showTable && (
                <EmployeesTable
                  toggleAllEmployee={toggleAllEmployee}
                  paginationCount={paginationCount}
                  toggleEmployee={toggleEmployee}
                  checkedCount={checkedCount}
                  isAllEmployeeChecked={isAllEmployeeChecked}
                />
              )}
            </Box>
          </FormStep>
          <TopupConfirmationModal
            showModal={showConfirmationModal}
            employeesCount={employeesCount}
            handleCloseModal={handleCloseModal}
            handleTopupRequest={handleTopupRequest}
            totalAmount={formattedTopupWithFees}
            isTopupPending={isTopupPending}
            showOnHoldText={isToggle}
            topupAmount={formattedTopup}
            isPendingBenefit={isPendingBenefit}
          />
        </>
      )}
    </>
  );
};
